// The "HELP CANADA BE THE FIRST EQUITY NATION" call-to-action block.
//
// Replaces the low-signal empty-state comment invite on the podcast + article
// threads. Rendered by `ThreadPanel` when a thread has no approved comments
// yet, and also stands alone at the tail of static/reference articles that
// don't carry their own discussion (e.g. Economic considerations).
//
// Buttons:
//   1. "Leave comment or question" — expands the local comment form when
//      the CTA lives inside a ThreadPanel. When rendered standalone
//      (`onLeaveComment` not provided), it anchors to Article II's thread
//      so the reader lands on the main manifesto discussion.
//   2. "To help and receive progress reports" — fires a `en:open-auth`
//      custom event with `mode="register"`; the Home page listens for it
//      and opens the Auth modal in Sign-up mode.

import { MessageSquare, Mail, HandHeart } from "lucide-react";

const CONTACT_EMAIL = "accountant@equitynation.ca";

export default function HelpCTA({ onLeaveComment, testId = "help-cta" }) {
    const handleLeaveComment = () => {
        if (typeof onLeaveComment === "function") {
            onLeaveComment();
            return;
        }
        // Standalone fallback — anchor to Article II's thread where a live
        // discussion is available.
        if (typeof window !== "undefined") {
            window.location.hash = "article-the-equity-nation";
        }
    };

    const handleJoinToHelp = () => {
        if (typeof window !== "undefined") {
            window.dispatchEvent(
                new CustomEvent("en:open-auth", { detail: { mode: "register" } }),
            );
        }
    };

    return (
        <div
            data-testid={testId}
            className="mt-8 md:mt-12 border-t border-[color:var(--charcoal)]/25 pt-10 md:pt-14"
        >
            <h2 className="font-display font-medium text-2xl md:text-3xl leading-snug tracking-[-0.01em] text-[color:var(--charcoal)] max-w-[22ch]">
                HELP CANADA BE THE FIRST EQUITY NATION
            </h2>

            <ol className="mt-8 md:mt-10 space-y-6 md:space-y-7">
                <li className="grid grid-cols-[auto_1fr] gap-4 md:gap-6 items-center">
                    <span className="font-display text-[color:var(--charcoal)] text-3xl md:text-4xl font-bold tabular-nums leading-none">
                        1.
                    </span>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
                        <span className="font-body text-lg md:text-xl text-[color:var(--charcoal)] leading-snug">
                            Leave comment or question — no ID required.
                        </span>
                        <button
                            type="button"
                            onClick={handleLeaveComment}
                            data-testid={`${testId}-leave-comment`}
                            className="animate-join-pulse inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 whitespace-nowrap font-mono font-semibold text-[12px] uppercase tracking-[0.22em] text-white bg-[color:var(--accent-violet)] hover:bg-[color:var(--accent-violet-dark)] shadow-[0_6px_18px_-6px_rgba(107,79,196,0.55)] hover:shadow-[0_10px_24px_-8px_rgba(107,79,196,0.7)] transition-all duration-200 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-violet)]/40"
                        >
                            <MessageSquare size={14} strokeWidth={1.8} />
                            Leave a comment
                        </button>
                    </div>
                </li>

                <li className="grid grid-cols-[auto_1fr] gap-4 md:gap-6 items-center">
                    <span className="font-display text-[color:var(--charcoal)] text-3xl md:text-4xl font-bold tabular-nums leading-none">
                        2.
                    </span>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
                        <span className="font-body text-lg md:text-xl text-[color:var(--charcoal)] leading-snug">
                            To help and receive progress reports.
                        </span>
                        <button
                            type="button"
                            onClick={handleJoinToHelp}
                            data-testid={`${testId}-join-to-help`}
                            className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 whitespace-nowrap font-mono font-semibold text-[12px] uppercase tracking-[0.22em] text-white bg-[color:var(--accent-violet)] hover:bg-[color:var(--accent-violet-dark)] shadow-[0_6px_18px_-6px_rgba(107,79,196,0.55)] hover:shadow-[0_10px_24px_-8px_rgba(107,79,196,0.7)] transition-all duration-200 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-violet)]/40"
                        >
                            <HandHeart size={14} strokeWidth={1.8} />
                            Join to help
                        </button>
                    </div>
                </li>

                <li className="grid grid-cols-[auto_1fr] gap-4 md:gap-6 items-start">
                    <span className="font-display text-[color:var(--charcoal)] text-3xl md:text-4xl font-bold tabular-nums leading-none">
                        3.
                    </span>
                    <div className="flex flex-col gap-1">
                        <div className="inline-flex items-center gap-2 font-body text-lg md:text-xl text-[color:var(--charcoal)]">
                            <Mail
                                size={18}
                                strokeWidth={1.7}
                                className="text-[color:var(--accent-violet)]"
                            />
                            <span>Email:</span>
                            <a
                                href={`mailto:${CONTACT_EMAIL}`}
                                data-testid={`${testId}-email-link`}
                                className="underline decoration-[color:var(--accent-violet)]/60 underline-offset-4 hover:decoration-[color:var(--accent-violet)] font-semibold"
                            >
                                {CONTACT_EMAIL}
                            </a>
                        </div>
                        <p className="ml-7 font-body text-[color:var(--charcoal)]/70 italic text-base md:text-lg leading-snug">
                            For direct contact to join and help.
                        </p>
                    </div>
                </li>
            </ol>
        </div>
    );
}
